import {
  Box,
  Button as ChakraButton,
  Center,
  Divider,
  Flex,
  Icon,
  IconButton,
  Skeleton,
  SkeletonText,
  Spinner,
  Stack,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import download from 'downloadjs';
import React, { useState } from 'react';
import { FiDownload } from 'react-icons/fi';
import { useHistory, useParams } from 'react-router-dom';
import { Heading, StatusBadge, Table } from '~/components';
import { ROUTER_PATHS } from '~/constants';
import { useInvoice } from '~/hooks';
import { getBillPdf, getInvoicePdf } from '~/services';
import { replaceParameters } from '~/utils/replaceParamaters';

export const InvoiceDetail: React.VFC = () => {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useInvoice(Number(id));
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  const [isDownloadingBill, setIsDownloadingBill] = useState(false);
  const history = useHistory();
  const toast = useToast();

  function handleInvoiceDownload(cd_fatura: string, nu_fatura: string) {
    setIsDownloadingInvoice(true);
    getInvoicePdf(cd_fatura)
      .then(response => {
        setIsDownloadingInvoice(false);
        download(
          atob(response?.data),
          `fatura_${cd_fatura}_${nu_fatura}`,
          'application/pdf',
        );
      })
      .catch((e: any) => {
        setIsDownloadingInvoice(false);
        if (e?.response?.status === 404) {
          console.log(e.response.data.message);
          toast({
            title: e.response.data.message,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else {
          toast({
            title: 'Falha ao baixar fatura',
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      });
  }

  function handleDownloadBill(cd_fatura: string, nu_fatura: string) {
    setIsDownloadingBill(true);
    getBillPdf(cd_fatura)
      .then(response => {
        setIsDownloadingBill(false);
        download(
          atob(response?.data),
          `boleto_${cd_fatura}_${nu_fatura}`,
          'application/pdf',
        );
      })
      .catch((e: any) => {
        setIsDownloadingBill(false);
        if (e?.response?.status === 404) {
          console.log(e.response.data.message);
          toast({
            title: e.response.data.message,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else {
          toast({
            title: 'Falha ao baixar boleto',
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      });
  }

  if (isLoading)
    return (
      <Box px="8" py="4">
        <Skeleton width="30%" height="20px" />
        <Flex
          flexDir="row"
          flexWrap="wrap"
          w="100%"
          py={9}
          gridGap="12"
          style={{
            position: 'relative',
          }}
        >
          <Stack maxW={400} w="100%" gridGap={4}>
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
          <Center
            position="relative"
            bottom="0"
            display={{ md: 'block', sm: 'none' }}
          />
          <Stack maxW={400} w="100%" gridGap={4}>
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
          <Center
            position="relative"
            bottom="0"
            display={{ md: 'block', sm: 'none' }}
          />
          <Stack maxW={400} w="100%" gridGap={4}>
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
        </Flex>
        <Flex flexFlow="column" maxW="1000px">
          <Stack>
            <Skeleton width="200px" height="20px" />
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
        </Flex>
        <Flex flexFlow="column" maxW="700px" mt="8">
          <Stack>
            <Skeleton width="200px" height="20px" />
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
        </Flex>
      </Box>
    );

  return (
    <Box px="8" py="4" overflowY="scroll" paddingBottom="8rem">
      <Heading hasBackButton>Fatura N° {data?.nu_fatura}</Heading>
      {data && (
        <Box px="2" py="2" overflowY="hidden">
          <Flex flexFlow="column" mt={4}>
            <Heading fontSize="md" mb={4}>
              Download de 2ª via
            </Heading>
            <Table size="small" maxW="min-content" type="secondary">
              <Thead>
                <Tr align="center">
                  <Th px="4" py="4">
                    <Center>Fatura</Center>
                  </Th>
                  <Th px="4" py="4">
                    <Center>Boleto</Center>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr key={data.id_fatura}>
                  <Td px="4" py="4">
                    <Center>
                      <Tooltip label="Baixar Fatura">
                        <IconButton
                          w="25px"
                          fontSize="20px"
                          background="transparent"
                          aria-label="Send email"
                          icon={
                            isDownloadingInvoice ? (
                              <Spinner />
                            ) : (
                              <Icon fontSize="20px" as={FiDownload} />
                            )
                          }
                          onClick={() =>
                            handleInvoiceDownload(
                              data.cd_fatura,
                              data.nu_fatura,
                            )
                          }
                        />
                      </Tooltip>
                    </Center>
                  </Td>
                  <Td px="4" py="4">
                    <Center>
                      <Tooltip label="Baixar Boleto">
                        <IconButton
                          disabled={data?.status === 1}
                          w="25px"
                          fontSize="20px"
                          background="transparent"
                          aria-label="Send email"
                          icon={
                            isDownloadingBill ? (
                              <Spinner />
                            ) : (
                              <Icon fontSize="20px" as={FiDownload} />
                            )
                          }
                          onClick={() =>
                            handleDownloadBill(data.cd_fatura, data.nu_fatura)
                          }
                        />
                      </Tooltip>
                    </Center>
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </Flex>
        </Box>
        // <HStack mt="20px" gap={4}>
        //   <Tooltip label="Baixar Fatura">
        //     <IconButton
        //       w="25px"
        //       fontSize="20px"
        //       background="transparent"
        //       aria-label="Send email"
        //       icon={
        //         isDownloadingInvoice ? <Spinner /> : <Icon as={FiDownload} />
        //       }
        //       onClick={() => handleInvoiceDownload(data.cd_fatura)}
        //     />
        //   </Tooltip>
        //   {
        //     /*! data?.status && */
        //     <Tooltip label="Baixar Boleto">
        //       <IconButton
        //         w="25px"
        //         fontSize="20px"
        //         background="transparent"
        //         aria-label="Send email"
        //         icon={
        //           isDownloadingBill ? <Spinner /> : <Icon as={FiDownload} />
        //         }
        //         onClick={() => handleDownloadBill(data.cd_fatura)}
        //       />
        //     </Tooltip>
        //   }
        // </HStack>
      )}
      <Flex
        flexDir="row"
        flexWrap="wrap"
        w="100%"
        py={9}
        gridGap="12"
        style={{
          position: 'relative',
        }}
      >
        <Stack maxW={400} w="100%" gridGap={4}>
          <Heading fontSize="md">Informações Gerais</Heading>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Código de Identificação:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.nu_fatura}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Data Emissão:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.formattedIssueDate}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Data do vencimento:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.formattedDueDate}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Valor:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.formattedValue}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Situação Pagamento:
            </Text>
            <Text color="gray.500" fontSize="14px">
              <StatusBadge variant={data?.colorScheme || ''}>
                {data?.formattedStatus}
              </StatusBadge>
            </Text>
          </Flex>
        </Stack>
        <Center
          position="relative"
          bottom="0"
          display={{ md: 'block', sm: 'none' }}
        >
          <Divider orientation="vertical" borderColor="gray.300" />
        </Center>
        <Stack maxW={400} w="100%" gridGap={4}>
          <Heading fontSize="md">Dados da Contratante</Heading>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Nome Fantasia:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.empresas[0]?.nm_fantasia || '-'}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Razão Social:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.empresas[0]?.nm_razaosocial || '-'}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              CNPJ:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.empresas[0]?.cnpjFormatado || '-'}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Grupo Econômico:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.empresas[0]?.grupoeconomico?.nm_grupoeconomico || '-'}
            </Text>
          </Flex>
        </Stack>
      </Flex>
      <Flex flexFlow="column" maxW="1000px" mt={4}>
        <Heading fontSize="md" mb={4}>
          Detalhamento da fatura
        </Heading>
        <Table type="secondary">
          <Thead>
            <Tr>
              <Th>N° Contrato</Th>
              <Th>N° Pedido Locação</Th>
              <Th>Valor</Th>
              <Th>Data Início</Th>
            </Tr>
          </Thead>
          <Tbody>
            {data?.contratos &&
              data?.contratos.map(contract => (
                <Tr key={contract.id_contrato}>
                  <Td>
                    <ChakraButton
                      variant="link"
                      fontWeight="normal"
                      color="#78797A"
                      onClick={() =>
                        history.push(
                          replaceParameters(
                            ROUTER_PATHS.PORTAL.CONCTRACT_DETAIL,
                            contract.id_contrato,
                          ),
                        )
                      }
                      fontSize="14px"
                    >
                      {contract.nu_contrato}
                    </ChakraButton>
                  </Td>
                  <Td fontSize="14px">{contract.nu_pedido}</Td>
                  <Td fontSize="14px">{contract.formattedMensalValue}</Td>
                  <Td fontSize="14px">{contract.formattedInitialDate}</Td>
                </Tr>
              ))}
          </Tbody>
        </Table>
      </Flex>
    </Box>
  );
};
